/**
 * @file PageView
 * @author Yu Zong(liuxuanzy@qq.com)
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useRefCallback } from '@tencent/glue-react-use';
import { shallowEqual } from '@tencent/glue-util-helper';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Device } from './modules/DeviceModule';
import { View } from './View';
export var PageView = forwardRef(function (props, ref) {
    var className = props.className, children = props.children, onPageLayout = props.onPageLayout, onContentLayout = props.onContentLayout, onLoad = props.onLoad, onScroll = props.onScroll, extra = __rest(props, ["className", "children", "onPageLayout", "onContentLayout", "onLoad", "onScroll"]);
    var waiting = useRef(false);
    var endScrollHandle = useRef(null);
    var onDocumentScroll = useRefCallback(function () {
        var doc = document.documentElement;
        if (waiting.current || !onScroll || !doc) {
            return;
        }
        waiting.current = true;
        if (endScrollHandle.current != null) {
            clearTimeout(endScrollHandle.current);
            endScrollHandle.current = null;
        }
        var ratio = Device.getRatio();
        var params = {
            x: (window.pageXOffset || doc.scrollLeft) / ratio,
            y: (window.pageYOffset || doc.scrollTop) / ratio,
            width: doc.clientWidth / ratio,
            height: doc.clientHeight / ratio,
            contentWidth: doc.scrollWidth / ratio,
            contentHeight: doc.scrollHeight / ratio,
        };
        onScroll(params);
        setTimeout(function () {
            waiting.current = false;
        }, 100);
        endScrollHandle.current = setTimeout(function () {
            var newParams = {
                x: (window.pageXOffset || doc.scrollLeft) / ratio,
                y: (window.pageYOffset || doc.scrollTop) / ratio,
                width: doc.clientWidth / ratio,
                height: doc.clientHeight / ratio,
                contentWidth: doc.scrollWidth / ratio,
                contentHeight: doc.scrollHeight / ratio,
            };
            if (!shallowEqual(newParams, params)) {
                onScroll(newParams);
            }
            endScrollHandle.current = null;
        }, 200);
    });
    var onLayoutCallback = useRefCallback(onPageLayout);
    useImperativeHandle(ref, function () { return ({
        scrollTo: function (x, y, animated) {
            if (endScrollHandle.current != null) {
                clearTimeout(endScrollHandle.current);
                endScrollHandle.current = null;
            }
            var ratio = Device.getRatio();
            document.documentElement.scrollTo({
                left: x * ratio,
                top: y * ratio,
                behavior: animated ? 'smooth' : 'auto',
            });
        },
    }); }, []);
    useEffect(function () {
        onLoad === null || onLoad === void 0 ? void 0 : onLoad();
        onLayoutCallback(Device.getVirtualSize());
        var onResize = function () {
            onLayoutCallback(Device.getVirtualSize());
        };
        document.addEventListener('scroll', onDocumentScroll, { passive: true });
        window.addEventListener('resize', onResize);
        return function () {
            document.removeEventListener('scroll', onDocumentScroll);
            window.removeEventListener('resize', onResize);
        };
    }, []);
    return (React.createElement(View, __assign({}, extra, { onLayout: onContentLayout, className: className ? "".concat(className, " xj-h5-page") : className }), children));
});
